/* eslint-disable max-statements */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { Button, Spin, message, notification } from 'antd';
import { filter, find, findIndex, forEach, isEmpty, map, toNumber } from 'lodash';

import {
  updateConnectionStatus,
  getConnectionReport,
  getOnboardingStatus,
  getConnections,
  submitUserFeedback,
  getSingleConnection,
} from 'Src/mentorship/actions';
import I18nCustomFormatter from 'Src/common/components/i18nCustomFormatter';
import history from 'Src/common/utilities/history';
import Wrapper from '../../../components/connections/wrapper';
import InfoCard from '../../../components/connections/infoCard';
import EmptyState from '../../../components/connections/emptyState';
import ConnectionCard from '../../../components/connections/connectionCard';
import MessagesModal from './messagesModal';
import RatingModal from './ratingModal';
import { addOrRemoveParams } from '../../../utils';
import { MESSAGES_MODAL_PARAM, FEEDBACK_MODAL_PARAM } from '../../../constants';

function Connections() {
  const [connectionReport, setConnectionReport] = useState({});
  const [openConnections, setOpenConnections] = useState([]);
  const [completedConnections, setCompletedConnections] = useState([]);
  const [onboardingDetails, setOnboardingDetails] = useState({});
  const [messagesConnectionData, setMessagesConnectionData] = useState({});
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hasMoreLoading, setHasMoreLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const isMentor = !!onboardingDetails?.form_1_data?.is_mentor;
  const isMentee = !!onboardingDetails?.form_1_data?.is_mentee;

  const handleConnectionsData = (connections) => {
    if (!isEmpty(connections)) {
      const completed = [];
      const open = [];

      forEach(connections, (connection) => {
        if (connection.status === 5) {
          completed.push(connection);
        } else {
          open.push(connection);
        }
      });

      setOpenConnections([...openConnections, ...open]);
      setCompletedConnections([...completedConnections, ...completed]);
    }
  };

  const handleParams = () => {
    const params = new URLSearchParams(window.location.search);

    if (params.has(MESSAGES_MODAL_PARAM)) {
      const id = params.get(MESSAGES_MODAL_PARAM);

      getSingleConnection(id).then((res) => {
        openMessages(res.data.results[0]);
      });
    } else if (params.has(FEEDBACK_MODAL_PARAM)) {
      const id = params.get(FEEDBACK_MODAL_PARAM);

      getSingleConnection(id).then((res) => {
        openRateConversationModal(res.data.results[0]);
      });
    }
  };

  useEffect(() => {
    const promises = [getConnectionReport(), getOnboardingStatus(), getConnections(1)];
    setLoading(true);

    Promise.all(promises)
      .then((results) => {
        const [connectionReportRes, onboardingStatusRes, connectionsRes] = results;

        if (!onboardingStatusRes.data.is_completed) {
          history.push('/mentorship/onboarding');
          return;
        }

        setOnboardingDetails(onboardingStatusRes.data);
        setConnectionReport(connectionReportRes.data);

        const connections = connectionsRes.data.results;
        handleConnectionsData(connections);

        if (connectionsRes.data.next) {
          setHasMore(true);
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    handleParams();
  }, []);

  const loadMoreConnections = () => {
    setHasMoreLoading(true);
    setCurrentPage((prev) => prev + 1);

    getConnections(currentPage + 1)
      .then((res) => {
        if (!res.data.next) {
          setHasMore(false);
        }

        handleConnectionsData(res.data.results);
        setHasMoreLoading(false);
      })
      .catch(() => {
        setHasMoreLoading(false);
      });
  };

  const updateWithStatus = (id, status) => {
    const connections = [...openConnections];
    const item = find(connections, (connection) => connection.id === id);
    item.status = status;

    setOpenConnections(connections);
  };

  const updateRatingObject = (connection, rating) => {
    const isUserMentor = window.userData.id === connection.mentor.id;

    if (isUserMentor) {
      connection.mentor.rating = rating;
    } else {
      connection.mentee.rating = rating;
    }

    return connection;
  };

  const updateRatingAfterComplete = (id, rating, allConnections, setter, status) => {
    const connectionToBeUpdated = find(allConnections, (connection) => connection.id === id);
    const connections = [...allConnections];
    const idx = findIndex(connections, (connection) => connection.id === id);

    updateRatingObject(connectionToBeUpdated, rating);
    connectionToBeUpdated.status = status;

    connections[idx] = connectionToBeUpdated;
    setter(connections);
  };

  const acceptRequest = (data) => {
    updateConnectionStatus(data.id, { status: 2 }).then(() => {
      updateWithStatus(data.id, 2);
    });
  };

  const withdrawRequest = (data) => {
    updateConnectionStatus(data.id, { status: 6 })
      .then(() => {
        setOpenConnections((prev) => filter(prev, (connection) => connection.id !== data.id));

        notification.success({
          message: 'Connection request withdrawn',
          description: (
            <p className="arc-p">
              You have {toNumber(connectionReport?.connections_available) + 1} connections remaining.
              <br />
              You can view your connections in the connections tab.
            </p>
          ),
        });

        setConnectionReport({ ...connectionReport, connections_available: connectionReport.connections_available + 1 });
      })
      .catch(() => message.error('Failed to withdraw request!'));
  };

  const declineRequest = (data) => {
    updateConnectionStatus(data.id, { status: 3 })
      .then(() => {
        setOpenConnections((prev) => filter(prev, (connection) => connection.id !== data.id));

        notification.success({
          message: 'Connection request declined',
          description: (
            <p className="arc-p">
              You have {connectionReport.connections_available + 1} connections remaining.
              <br />
              You can view your connections in the connections tab.
            </p>
          ),
        });

        setConnectionReport({ ...connectionReport, connections_available: connectionReport.connections_available + 1 });
      })
      .catch(() => message.error('Failed to decline request!'));
  };

  function openRateConversationModal(data) {
    setIsRatingModalOpen(true);
    setMessagesConnectionData(data);
    addOrRemoveParams({ key: FEEDBACK_MODAL_PARAM, value: data.id, action: 'add' });
  }

  function closeConnection(data) {
    openRateConversationModal({ ...data, closing: true });
  }

  function openMessages(data) {
    setIsMessagesModalOpen(true);
    setMessagesConnectionData(data);
    addOrRemoveParams({ key: MESSAGES_MODAL_PARAM, value: data.id, action: 'add' });
  }

  function closeMessagesModal() {
    setIsMessagesModalOpen(false);
    setMessagesConnectionData({});
    addOrRemoveParams({ key: MESSAGES_MODAL_PARAM, action: 'remove' });
  }

  function closeRateConversationModal() {
    setIsRatingModalOpen(false);
    setMessagesConnectionData({});
    addOrRemoveParams({ key: FEEDBACK_MODAL_PARAM, action: 'remove' });
  }

  function submitRating(feedback) {
    const { connection } = feedback;
    const isUserMentor = window.userData?.id === connection.mentor.id;
    const questions = isUserMentor ? window.mentorFeedbackQuestions : window.menteeFeedbackQuestions;

    const params = {
      parent_object_id: connection.id,
      content_type: isUserMentor ? 'mentor' : 'mentee',
      object_id: isUserMentor ? connection.mentor.mentor_id : connection.mentee.mentee_id,
    };
    const data = [
      {
        rating: feedback.rating,
        comment: feedback.comment,
        selected_reasons: [],
        question: { id: questions[0].id },
      },
    ];

    if (connection.status === 5) {
      submitUserFeedback(params, data).then(() => {
        updateRatingAfterComplete(connection.id, feedback.rating, completedConnections, setCompletedConnections, 5);
        closeRateConversationModal();
        notification.success({
          message: 'Feedback submitted successfully',
        });
      });
    } else {
      updateConnectionStatus(connection.id, { status: 5 })
        .then(() => {
          submitUserFeedback(params, data)
            .then(() => {
              updateRatingAfterComplete(connection.id, feedback.rating, openConnections, setOpenConnections, 5);
              closeRateConversationModal();

              notification.success({
                message: 'Connection closed ',
                description: <p className="arc-p">Feel free to explore and connect with other members.</p>,
              });

              setConnectionReport({
                ...connectionReport,
                connections_available: connectionReport.connections_available + 1,
              });
            })
            .catch(() => message.error('Failed to submit feedback!'));
        })
        .catch(() => message.error('Failed to close connection!'));
    }
  }

  if (loading) {
    return <Spin />;
  }

  return (
    <Wrapper>
      <InfoCard isMentor={false} connectionReport={connectionReport} showButton={isMentee} />
      <Choose>
        <When condition={isEmpty(openConnections) && isEmpty(completedConnections)}>
          <If condition={isMentor}>
            <InfoCard isMentor connectionReport={connectionReport} />
          </If>
          <EmptyState isMentor={isMentor} />
        </When>
        <Otherwise>
          {map(openConnections, (data) => (
            <ConnectionCard
              key={data.id}
              data={data}
              acceptRequest={acceptRequest}
              declineRequest={declineRequest}
              withdrawRequest={withdrawRequest}
              closeConnection={closeConnection}
              openMessages={openMessages}
              openRateConversationModal={openRateConversationModal}
            />
          ))}
          <p className="arc-p">Connection history</p>
          {map(completedConnections, (data) => (
            <ConnectionCard
              key={data.id}
              data={data}
              acceptRequest={acceptRequest}
              declineRequest={declineRequest}
              withdrawRequest={withdrawRequest}
              openMessages={openMessages}
              openRateConversationModal={openRateConversationModal}
            />
          ))}

          <If condition={hasMore}>
            <Button
              type="primary"
              className="connections-load-more-btn"
              onClick={loadMoreConnections}
              loading={hasMoreLoading}>
              <I18nCustomFormatter id="load-more-button" />
            </Button>
          </If>

          <If condition={isMessagesModalOpen}>
            <MessagesModal
              isOpen={isMessagesModalOpen}
              closeMessagesModal={closeMessagesModal}
              connection={messagesConnectionData}
            />
          </If>
          <If condition={isRatingModalOpen}>
            <RatingModal
              isOpen={isRatingModalOpen}
              closeRatingModal={closeRateConversationModal}
              connection={messagesConnectionData}
              submitRating={submitRating}
            />
          </If>
        </Otherwise>
      </Choose>
    </Wrapper>
  );
}

export default Connections;
